import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { formatMobile, formatName } from "../../../lib/Misc";
import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import AddEditUser from "./AddEditUser";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import UserActionButton from "./UserActionButton";
import Confirmation from "../../../components/Confirmation";
import { useOrganization } from "../../../lib/context/OrganisationContext";

const Users = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { organizationData } = useOrganization();

  const [userId, setUserId] = useState(null);
  const [showAddEditUser, setShowAddEditUser] = useState(false);
  const [showUserDelete, setShowUserDelete] = useState(false);

  const toggleShowAddEditUser = () => {
    if (showAddEditUser) {
      setEdit(false);
      setUserId(null);
    }
    setShowAddEditUser(!showAddEditUser);
  };

  const toggleShowDelete = () => {
    if (showUserDelete) {
      setUserId(null);
    }
    setShowUserDelete(!showUserDelete);
  };

  const fetchUser = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_USER_LIST}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const usersList = data.data.data;
        setUsersList(usersList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setUsersList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField]
  );

  const usersGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: usersList[i]?.id,
        name: formatName(usersList[i]?.first_name, usersList[i]?.last_name),
        email: usersList[i]?.email,
        mobile: usersList[i]?.mobile ? formatMobile(usersList[i]?.mobile) : "-",
        role: usersList[i]?.role?.name ? usersList[i]?.role?.name : "",
        action: (
          <>
            <UserActionButton
              id={usersList[i]?.id}
              setEdit={setEdit}
              setUserId={setUserId}
              toggleShowAddEditUser={toggleShowAddEditUser}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const users = usersGenerator(usersList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "email",
      text: "Email",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "mobile",
      text: "Mobile No.",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "role",
      text: "Role",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchUser(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchUser(limit, page);
  };

  // Single User Delete
  const handleUserDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.GET_ADMIN_USER_LIST}/${userId}`);
      setLoading(false);
      fetchUser();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Add User */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit User" : "Add User"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setUserId(null);
                setShowAddEditUser(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add User</span>
                </div>
              }
              className={""}
              open={showAddEditUser}
              buttonClassName={""}
              setOpen={setShowAddEditUser}
            >
              <AddEditUser
                edit={edit}
                toggleShowAddEditUser={toggleShowAddEditUser}
                id={userId}
                orgId={
                  organizationData?.organizationId
                    ? organizationData?.organizationId
                    : ""
                }
                fetchUserData={() => fetchUser(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={users}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete User"}
        open={showUserDelete}
        setOpen={setShowUserDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete user?"}
        okAction={async () => {
          handleUserDelete();
          toggleShowDelete();
          fetchUser();
        }}
        cancelAction={() => {
          setUserId(null);
          setShowUserDelete(!showUserDelete);
        }}
      />
    </>
  );
};

export default Users;
